import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { catchError, EMPTY, from, map, Observable, of, switchMap, timer } from "rxjs";

@Injectable({
    providedIn: 'root'
})

export class SmartyService {
    private credentials: any;
    private smartyAutoCompleteClient: any;
    private smartyStreetClient: any;
    private typingTimer: any;
    listOfSearchOptions: { value: string; text: string }[] = [];
    constructor(
        private httpClient: HttpClient,
    ) {
        const smartyKey = environment.smartyEmbeddedKey;
        this.credentials = new SmartySDK.core.SharedCredentials(smartyKey);
    }

    onSearch(value: string): Observable<{ value: string; text: string }[]> {
        if (!value || value.length <= 3) {
            return EMPTY; // no emission for short input
        }

        // create a 500ms debounce using timer
        return timer(500).pipe(
            switchMap(() => {
                const client = this.getAutocompleteClient();
                const Lookup = SmartySDK.usAutocompletePro.Lookup;
                const lookup = new Lookup(value);
                lookup.maxResults = 10;

                return from(client.send(lookup)).pipe(
                    map((response: any) => {
                        if (response.result && response.result.length > 0) {
                            const listOfSearchOptions = response.result.map((result: any) => ({
                                value: result,
                                text: `${result.streetLine}, ${result.city}, ${result.state} ${result.zipcode}`
                            }));

                            this.listOfSearchOptions = listOfSearchOptions;
                            return listOfSearchOptions;
                        }
                        return []; // empty array if no results
                    }),
                    catchError(err => {
                        console.error('Smarty autocomplete error:', err);
                        return of([]); // emit empty array on error
                    })
                );
            })
        );
    }
    
    async validateAddress(value: any) {
        const client = this.getUsStreetClient();
        const Lookup = SmartySDK.usStreet.Lookup;
        const lookup = new Lookup();
        lookup.street = value.streetLine;
        lookup.city = value.city;
        lookup.state = value.state;
        lookup.zipCode = value.zipcode;
        lookup.maxCandidates = 1;

        try {
            const response = await client.send(lookup);
            if (response?.lookups[0]?.result[0]) {
                const { deliveryLine1, components, metadata } = response.lookups[0].result[0];
                const zipCode = components.plus4Code ? `${components.zipCode}-${components.plus4Code}` : `${components.zipCode}`;
                const address = {
                    AddressLine1: deliveryLine1,
                    City: components.cityName,
                    PostalCode: zipCode,
                    State: components.state,
                    County: metadata.countyName,
                    CompleteAddress: `${deliveryLine1}, ${components.cityName}, ${components.state} ${zipCode}`,
                }
                this.extractSmartyAddress(address);
            }
        } catch (error: any) {
            console.error('Smarty usStreet error:', error);
            return;
        }
    }

    getUsStreetClient(): any {
        if (!this.smartyStreetClient) {
            this.smartyStreetClient = SmartySDK.core.buildClient.usStreet(this.credentials);
        }

        return this.smartyStreetClient;
    }

    getAutocompleteClient(): any {
        if (!this.smartyAutoCompleteClient) {
            this.smartyAutoCompleteClient = SmartySDK.core.buildClient.usAutocompletePro(this.credentials);
        }

        return this.smartyAutoCompleteClient;
    }

    extractSmartyAddress(address: {
        AddressLine1: string,
        City: string,
        PostalCode: string,
        State: string,
        County: string,
        CompleteAddress: string;
    }) {
        const selectedAddress = {
            street1: address.AddressLine1,
            street2: '',
            zipCode: address.PostalCode,
            state: address.State,
            city: address.City,
            countyOrRegion: address.County,
        };
    }
}